/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  IconBuildingWarehouse,
  IconCalendar,
  IconCar,
  IconClock,
  IconCloudDown,
  IconContract,
  IconFileText,
  IconFolderCode,
  IconId,
  IconMail,
  IconMap,
  IconMapPin,
  IconPhone,
  IconRoute,
  IconUser,
  IconWorld,
  IconEdit,
  IconX,
  IconClipboardCheck,
} from "@tabler/icons-react";

import NewCommonModal from "../../../components/common/NewCommonModal";
import { useForm } from "react-hook-form";
import formatDate from "../../../utilities/helper";
import toast from "react-hot-toast";
import { usePermission } from "../../../hooks/usePermissionTabs";
import AddDriverModal from "./AddDriverModal";
import {
  useDriverUpdateMutation,
  useFetchDriverDetailsData,
} from "../../../apis/driverApis";
import { T, useLanguage } from "../../../context/LanguageContext";

type Props = {
  id: string;
  open: boolean;
  onClose: () => void;
};

interface DocumentLinkProps {
  value?: string;
  docUrl?: string;
  label: string;
}

const DocumentLink = ({ value, docUrl, label }: DocumentLinkProps) => {
  if (!value && !docUrl) return null;

  return (
    <div className="flex items-center gap-2">
      {value && <span className="font-medium text-gray-800">{value}</span>}
      {docUrl && (
        <a
          href={docUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-cente hover:bg-[#FBD037]/20 p-0.5 px-1 rounded"
          title={`View ${label} Document`}
          aria-label={`View ${label} Document`}
        >
          <IconCloudDown strokeWidth={1.5} size={22} />
        </a>
      )}
    </div>
  );
};

const titleColors: Record<string, string> = {
  "Workplace Details": "text-orange-600",
  Documents: "text-green-600",
  "Vehicle Info": "text-purple-600",
  Location: "text-blue-600",
};

const InfoBlock = ({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div>
    <h4
      className={`text-sm font-semibold text-[#212121] mb-2 uppercase tracking-wide ${
        titleColors[title] || "text-gray-600"
      } `}
    >
      {title}
    </h4>
    <div className={` ${className || "space-y-2"}`}>{children}</div>
  </div>
);

const Row = ({
  icon,
  label,
  value,
  icon2,
  className,
}: {
  icon?: React.ReactNode;
  icon2?: React.ReactNode;
  label: string;
  className?: string;
  value: React.ReactNode;
}) => (
  <div className={`flex items-center justify-between gap-2 ${className}`}>
    <div className="flex items-center gap-2">
      {icon && <div className="mt-0.5 text-gray-500">{icon}</div>}
      <span className="font-medium text-sm text-[#212121]"><T>{label}</T>: </span>
    </div>
    <div className="flex items-center gap-2">
      <span className="break-all text-xs">{value}</span>
      {icon2 && <div className="mt-0.5 text-gray-500">{icon2}</div>}
    </div>
  </div>
);

const TabButton = ({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${
      active
        ? "bg-[#FBD037]/20 text-[#000000] shadow-sm border"
        : "bg-slate-100 text-slate-600 border"
    }`}
  >
    {icon}
    <T>{label}</T>
  </button>
);

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      <T>{title}</T>
    </p>
  </div>
);

export default function ViewDriverModal({ id, open, onClose }: Props) {
  const { t } = useLanguage();
  const {
    data: driverDetails,
    refetch: fetchDriverDetails,
    isFetching,
  } = useFetchDriverDetailsData(id ?? "");

  const [editDialog, setEditDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "personal" | "workplace" | "vehicle"
  >("personal");

  const hasVehicleDetails =
    driverDetails?.vehicle_type ||
    driverDetails?.vehicleName ||
    driverDetails?.vehicle_number ||
    driverDetails?.assigned_on;

  const [selectedDocument, setSelectedDocument] = useState<any>({
    iqamaDoc: false,
    licenseDoc: false,
    image: false,
  });

  const { mutate, isPending: isRequestPending } = useDriverUpdateMutation(
    id ?? "",
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    control,
    reset,
    formState: { errors },
  } = useForm<any>();

  useEffect(() => {
    if (id) {
      fetchDriverDetails();
    }
  }, [id]);

  const handleEditDriverToggle = () => {
    setEditDialog(!editDialog);
    if (!editDialog) {
      reset();
    }
  };

  const { allowed: canUpdateDriver } = usePermission("drivers", "update");

  const handleDriverUpdate = (data: any) => {
    let { image, iqamaDoc, licenseDoc, ...otherData } = data;
    mutate(
      {
        ...otherData,
        ...(selectedDocument?.iqamaDoc && { iqamaDoc: data?.iqamaDoc }),
        ...(selectedDocument?.licenseDoc && { licenseDoc: data?.licenseDoc }),
        ...(selectedDocument?.photo && { image: data?.image }),
      },
      {
        onSuccess: () => {
          setSelectedDocument({
            iqamaDoc: false,
            licenseDoc: false,
            image: false,
          });
          fetchDriverDetails();
          handleEditDriverToggle();
          setEditDialog(false);
          toast.success("Driver Update Successfully.");
        },
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            (typeof error === "string" ? error : null) ||
            "Failed to Update Driver";

          toast.error(errorMessage);
        },
      },
    );
  };
  const smartEmail = (email?: string) => {
    if (!email) return "";

    const [name, domain] = email.split("@");
    if (!domain) return email;

    const [domainName, extension] = domain.split(".");

    const shortName = name.length > 5 ? `${name.slice(0, 4)}...` : name;

    const shortDomain =
      domainName && domainName.length > 4
        ? `${domainName.slice(0, 3)}...`
        : domainName;

    return `${shortName}@${shortDomain}.${extension || ""}`;
  };

  if (!open) return null;

  const driverImage =
    driverDetails?.image &&
    driverDetails.image !== "null" &&
    driverDetails.image !== "undefined" &&
    driverDetails.image !== ""
      ? driverDetails.image
      : "/img/dummy-blank-profile.png";

  return (
    <div className="relative bg-white rounded-3xl shadow-sm border flex flex-col overflow-hidden h-full">
      {/* Modal Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <div className="w-14 h-14 rounded-xl overflow-hidden">
            <img
              src={driverImage}
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "/img/dummy-blank-profile.png";
              }}
              alt="Driver Profile"
              className="w-full h-full object-cover rounded-xl"
            />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[#212121]">
              {driverDetails?.driverName || t("Driver Details")}
            </h2>
            <p className="text-sm text-[#212121]/70">
              {driverDetails?.driver_code || "-"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!editDialog && canUpdateDriver && (
            <button
              onClick={handleEditDriverToggle}
              className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
              title={t("Edit Driver")}
            >
              <IconEdit size={18} />
            </button>
          )}
          {editDialog && (
            <button
              onClick={handleSubmit(handleDriverUpdate)}
              disabled={isRequestPending}
              className="px-4 py-2 bg-[#FBD037] text-[#212121] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRequestPending ? t("Updating...") : t("Update")}
            </button>
          )}
          <button
            onClick={() => {
              setEditDialog(false);
              setSelectedDocument({
                iqamaDoc: false,
                licenseDoc: false,
                image: false,
              });
              reset({});
              onClose();
            }}
            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      {/* Modal Body */}
      <div className="flex-1 overflow-y-auto">
        {isFetching ? (
          <div className="w-full space-y-4 p-10 text-center">
            <div className="animate-pulse">{t("Loading...")}</div>
          </div>
        ) : editDialog ? (
          <div className="py-2">
            <AddDriverModal
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
              clearErrors={clearErrors}
              control={control}
              setSelectedDocument={setSelectedDocument}
              driverData={driverDetails}
            />
          </div>
        ) : (
          <div className="relative p-6 bg-white">
            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b border-gray-200/50 pb-3">
              <TabButton
                active={activeTab === "personal"}
                onClick={() => setActiveTab("personal")}
                icon={<IconUser size={14} />}
                label="Personal Info"
              />
              <TabButton
                active={activeTab === "workplace"}
                onClick={() => setActiveTab("workplace")}
                icon={<IconBuildingWarehouse size={14} />}
                label="Workplace"
              />
              {hasVehicleDetails && (
                <TabButton
                  active={activeTab === "vehicle"}
                  onClick={() => setActiveTab("vehicle")}
                  icon={<IconCar size={14} />}
                  label="Vehicle Info"
                />
              )}
            </div>

            {/* Tab content conditional rendering */}
            {activeTab === "personal" && (
              <div className="space-y-6">
                {/* Personal Details Card */}
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader icon={IconUser} title="Personal Details" />

                  <div className="space-y-2">
                    <Row
                      icon={<IconId size={18} />}
                      label="Driver Code"
                      value={driverDetails?.driver_code || "-"}
                    />
                    <Row
                      icon={<IconPhone size={18} />}
                      label="Mobile"
                      value={driverDetails?.mobile_no || "-"}
                    />
                    <Row
                      icon={<IconMail size={18} />}
                      label="Email"
                      value={driverDetails?.email || "-"}
                    />
                    <Row
                      icon={<IconCalendar size={18} />}
                      label="Joined"
                      value={formatDate(driverDetails?.start_date)}
                    />
                    <Row
                      icon={<IconUser size={18} />}
                      label="Driver Type"
                      value={driverDetails?.driver_type || "-"}
                    />
                    <Row
                      icon={<IconRoute size={18} />}
                      label="Route Code"
                      value={driverDetails?.route_code || "-"}
                    />
                    {/* <Row
                      icon={<IconUser size={18} />}
                      label="Status"
                      value={
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                            driverDetails?.status === "ACTIVE"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-600"
                          }`}
                        >
                          {driverDetails?.status}
                        </span>
                      }
                    /> */}
                  </div>
                </div>

                {/* Documents Card */}
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader icon={IconFileText} title="Documents" />

                  <div className="space-y-2">
                    <Row
                      icon={<IconId size={18} />}
                      label="Iqama"
                      value={
                        <DocumentLink
                          value={driverDetails?.iqama_number}
                          docUrl={driverDetails?.iqama_doc}
                          label="Iqama"
                        />
                      }
                    />

                    <Row
                      icon={<IconFileText size={18} />}
                      label="License"
                      value={
                        <DocumentLink
                          value={driverDetails?.license_number}
                          docUrl={driverDetails?.license_doc}
                          label="License"
                        />
                      }
                    />

                    <Row
                      icon={<IconCar size={18} />}
                      label="RC"
                      value={
                        <DocumentLink
                          value={driverDetails?.rc_number}
                          docUrl={driverDetails?.rc_docs}
                          label="RC"
                        />
                      }
                    />

                    <Row
                      icon={<IconClipboardCheck size={18} />}
                      label="Vehicle Inspection"
                      value={
                        <DocumentLink
                          docUrl={driverDetails?.inspection_paper}
                          label="Vehicle Inspection"
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "workplace" && (
              <div className="space-y-6">
                {/* Workplace Details Card */}
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader
                    icon={IconBuildingWarehouse}
                    title="Workplace Details"
                  />
                  <div className="space-y-2">
                    <Row
                      icon={<IconBuildingWarehouse size={18} />}
                      label="Warehouse"
                      value={driverDetails?.warehouseName || "-"}
                    />
                    <Row
                      icon={<IconClock size={18} />}
                      label="Time Slot"
                      value={driverDetails?.timeslotname || "-"}
                    />
                    {driverDetails?.darkStoreName && (
                      <Row
                        icon={<IconBuildingWarehouse size={18} />}
                        label="Dark Store"
                        value={driverDetails?.darkStoreName}
                      />
                    )}
                  </div>
                </div>

                {/* Location Card */}
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader icon={IconMapPin} title="Location" />

                  <div className="space-y-2">
                    <Row
                      icon={<IconWorld size={18} />}
                      label="Country"
                      value={driverDetails?.countryName || "-"}
                    />
                    <Row
                      icon={<IconMap size={18} />}
                      label="City"
                      value={driverDetails?.cityName || "-"}
                    />
                    <Row
                      icon={<IconMapPin size={18} />}
                      label="District"
                      value={driverDetails?.districtName || "-"}
                    />
                    <Row
                      icon={<IconMapPin size={18} />}
                      label="Address"
                      value={driverDetails?.address || "-"}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "vehicle" && (
              <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                <SectionHeader icon={IconCar} title="Vehicle Info" />

                <div className="space-y-2">
                  <Row
                    icon={<IconCar size={18} />}
                    label="Vehicle Type"
                    value={driverDetails?.vehicle_type || "-"}
                  />
                  <Row
                    icon={<IconCar size={18} />}
                    label="Vehicle Name"
                    value={driverDetails?.vehicleName || "-"}
                  />
                  <Row
                    icon={<IconId size={18} />}
                    label="Vehicle Number"
                    value={driverDetails?.vehicle_number || "-"}
                  />
                  <Row
                    icon={<IconCalendar size={18} />}
                    label="Assigned Date"
                    value={formatDate(driverDetails?.assigned_on)}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
